import { IForumTopic } from '@dtos/Comment'
import {
  dedupeById,
  sortPostsByCreatedAtAsc,
  useGetForumTopic,
} from '@services/api/graphql/comments'
import { formatRelative } from '@utils/dateFormatter'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, Pressable } from 'react-native'
import { Text, XStack, YStack } from 'tamagui'
import { AuthorBadge } from './AuthorBadge'
import { PostForm } from './PostForm'
import { PostItem } from './PostItem'

type ToastType = 'success' | 'error'

interface ITopicItemProps {
  topic: IForumTopic
  onToast?: (type: ToastType, message: string) => void
}

export function TopicItem({ topic, onToast }: ITopicItemProps) {
  const { t } = useTranslation('Comments')
  const [showReplies, setShowReplies] = useState(false)
  const [showReplyForm, setShowReplyForm] = useState(false)

  // Só busca os posts quando o usuário pede para ver as respostas ou abre o
  // formulário de resposta.
  const shouldLoadPosts = showReplies || showReplyForm

  const { data, loading } = useGetForumTopic({
    id: topic.id,
    postsLimit: 50,
    skip: !shouldLoadPosts,
  })

  // Respostas em ordem cronológica (mais antiga primeiro) e sem duplicatas.
  const posts = useMemo(
    () => dedupeById(sortPostsByCreatedAtAsc(data?.forumTopic?.posts ?? [])),
    [data?.forumTopic?.posts],
  )

  const repliesCount = topic.postsCount ?? 0

  return (
    <YStack
      gap="$spacing-inset-sm"
      p="$spacing-inset-md"
      borderRadius="$border-radius-md"
      backgroundColor="$bg-surface"
      testID={`forum-topic-${topic.id}`}
    >
      <AuthorBadge user={topic.user} />

      <Text
        fontFamily="$mulishBold"
        fontSize="$font-size-xxs"
        color="$text-primary"
      >
        {topic.title}
      </Text>

      {!!topic.description && (
        <Text
          fontFamily="$mulishRegular"
          fontSize="$font-size-xxs"
          color="$text-primary"
        >
          {topic.description}
        </Text>
      )}

      <XStack alignItems="center" gap="$spacing-inset-xs">
        <Text
          fontFamily="$mulishRegular"
          fontSize="$font-size-xxxs"
          color="$text-placeholder"
        >
          {formatRelative(topic.createdAt)}
        </Text>

        {repliesCount > 0 && (
          <Pressable
            onPress={() => setShowReplies((prev) => !prev)}
            testID={`forum-topic-toggle-replies-${topic.id}`}
          >
            <Text
              fontFamily="$mulishBold"
              fontSize="$font-size-xxxs"
              color="$accent"
            >
              {showReplies
                ? t('topic.hideReplies')
                : repliesCount === 1
                  ? t('topic.repliesCount_one', { count: repliesCount })
                  : t('topic.repliesCount_other', { count: repliesCount })}
            </Text>
          </Pressable>
        )}

        <Pressable
          onPress={() => setShowReplyForm((prev) => !prev)}
          testID={`forum-topic-toggle-${topic.id}`}
        >
          <Text
            fontFamily="$mulishBold"
            fontSize="$font-size-xxxs"
            color="$accent"
          >
            {t('topic.reply')}
          </Text>
        </Pressable>
      </XStack>

      {shouldLoadPosts && (
        <YStack gap="$spacing-inset-sm" pt="$spacing-inset-xs">
          {loading && posts.length === 0 && (
            <YStack alignItems="center" py="$spacing-inset-sm">
              <ActivityIndicator />
            </YStack>
          )}
          {showReplies &&
            posts.map((post) => <PostItem key={post.id} post={post} />)}
          {showReplyForm && (
            <PostForm
              topicId={topic.id}
              onToast={onToast}
              onCreated={() => setShowReplies(true)}
            />
          )}
        </YStack>
      )}
    </YStack>
  )
}
